import { supabase } from '../lib/supabase';
import { PaymentRecord, SubscriptionDetails } from '../types/billing';

/**
 * Get payment history for a user.
 */
export async function getPaymentHistory(
  userId: string,
  limit: number = 5
): Promise<{ success: boolean; data?: PaymentRecord[]; error?: string }> {
  try {
    // Query the payments table (populated by verify-payment edge function)
    const { data, error } = await (supabase.client.from as any)('payments')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.warn('Payments table not available:', error.message);
      return { success: true, data: [] };
    }

    const records: PaymentRecord[] = (data || []).map((row: any) => ({
      id: row.id,
      amount: (row.amount || 0) / 100, // Convert from cents to dollars
      currency: (row.currency || 'usd').toUpperCase(),
      description: row.property_address || 'Gas Station Valuation Report',
      paymentMethod: '',
      status: row.status,
      createdAt: row.created_at,
    }));

    return { success: true, data: records };
  } catch (err) {
    console.error('Error fetching payment history:', err);
    return { success: true, data: [] };
  }
}

/**
 * Get subscription details from the user's profile.
 */
export async function getSubscriptionDetails(
  userId: string
): Promise<{ success: boolean; data?: SubscriptionDetails; error?: string }> {
  try {
    const { data, error } = await supabase.client
      .from('profiles')
      .select('subscription_tier, subscription_ends_at')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.warn('Error fetching subscription details:', error.message);
      return {
        success: true,
        data: {
          tier: 'free',
          endsAt: null,
          paymentMethodLast4: null,
          stripeCustomerId: null,
        },
      };
    }

    const profile = data as any;

    return {
      success: true,
      data: {
        tier: profile?.subscription_tier || 'free',
        endsAt: profile?.subscription_ends_at || null,
        paymentMethodLast4: profile?.payment_method_last4 || null,
        stripeCustomerId: profile?.stripe_customer_id || null,
      },
    };
  } catch (err) {
    console.error('Error fetching subscription:', err);
    return {
      success: true,
      data: {
        tier: 'free',
        endsAt: null,
        paymentMethodLast4: null,
        stripeCustomerId: null,
      },
    };
  }
}
